import React from 'react';
import { Button } from 'semantic-ui-react';
import Swal from 'sweetalert2';
import { resizeImageFile } from 'src/components/helpers';
import { MAX_CATALOG_IMAGES } from 'src/containers/comunidad-network/form/networkFormUtils';

const filesFromDrop = (event) => Array.from(event.dataTransfer?.files || []);

const useDropHighlight = () => {
  const depth = React.useRef(0);
  const [dragOver, setDragOver] = React.useState(false);

  const onDragEnter = (event) => {
    event.preventDefault();
    event.stopPropagation();
    depth.current += 1;
    setDragOver(true);
  };

  const onDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
  };

  const onDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    depth.current = Math.max(0, depth.current - 1);
    if (depth.current === 0) setDragOver(false);
  };

  const endDrag = () => {
    depth.current = 0;
    setDragOver(false);
  };

  return { dragOver, onDragEnter, onDragOver, onDragLeave, endDrag };
};

const NetworkFormMedia = ({
  disabled,
  logoPreview,
  catalogItems,
  onLogoSelected,
  onLogoClear,
  onCatalogSelected,
  onCatalogRemove,
}) => {
  const logoInputRef = React.useRef(null);
  const catalogInputRef = React.useRef(null);
  const logoDrop = useDropHighlight();
  const catalogDrop = useDropHighlight();
  const catalogCount = catalogItems.length;
  const catalogFull = catalogCount >= MAX_CATALOG_IMAGES;

  const applyLogo = async (file) => {
    if (!file) return;
    try {
      const resized = await resizeImageFile(file, { maxWidth: 800, maxHeight: 800 });
      onLogoSelected(resized, URL.createObjectURL(file));
    } catch (error) {
      Swal.fire(error.message || 'No se pudo cargar la imagen.');
    }
  };

  const applyCatalog = async (selected) => {
    if (!selected.length) return;

    const room = MAX_CATALOG_IMAGES - catalogCount;
    if (room <= 0) {
      Swal.fire('Puedes subir máximo 5 fotos del catálogo.');
      return;
    }
    if (selected.length > room) {
      Swal.fire(`Solo se agregaron ${room} fotos. El máximo es 5.`);
    }

    const next = [];
    for (const file of selected.slice(0, room)) {
      try {
        const resized = await resizeImageFile(file, { maxWidth: 1200, maxHeight: 1200 });
        next.push({
          key: `${Date.now()}-${next.length}-${file.name}`,
          file: resized,
          preview: URL.createObjectURL(file),
        });
      } catch (error) {
        Swal.fire(error.message || 'No se pudo cargar una de las fotos.');
      }
    }
    if (next.length) onCatalogSelected(next);
  };

  const readLogo = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    await applyLogo(file);
  };

  const readCatalog = async (event) => {
    const selected = Array.from(event.target.files || []);
    event.target.value = '';
    await applyCatalog(selected);
  };

  const dropLogo = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    logoDrop.endDrag();
    if (disabled) return;
    const files = filesFromDrop(event);
    if (files.length > 1) {
      Swal.fire('Solo se usó la primera imagen para el logo o la foto personal.');
    }
    await applyLogo(files[0]);
  };

  const dropCatalog = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    catalogDrop.endDrag();
    if (disabled) return;
    await applyCatalog(filesFromDrop(event));
  };

  return (
    <section className="form-section">
      <h3 className="form-section__title">Imágenes</h3>
      <div className="form-section__body">
        <div className="media-fields">
          <div className="media-block">
            <p className="media-block__label">Logo o foto personal</p>
            <p className="media-block__hint">
              Arrastra una imagen aquí o haz clic para subirla. JPG o PNG, máximo 5 Mb.
            </p>
            <label
              className={`media-drop ${logoDrop.dragOver ? 'is-dragover' : ''} ${
                disabled ? 'is-disabled' : ''
              }`}
              onDragEnter={logoDrop.onDragEnter}
              onDragOver={logoDrop.onDragOver}
              onDragLeave={logoDrop.onDragLeave}
              onDrop={dropLogo}
            >
              <div className={`media-tile ${logoPreview ? 'has-image' : ''}`}>
                {logoPreview ? (
                  <img src={logoPreview} alt="Logo o foto personal" />
                ) : (
                  <span>Arrastra o haz clic</span>
                )}
              </div>
              <input
                ref={logoInputRef}
                type="file"
                accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                hidden
                disabled={disabled}
                onChange={readLogo}
              />
            </label>
            <div className="media-block__actions">
              <Button
                type="button"
                primary
                size="small"
                disabled={disabled}
                onClick={() => logoInputRef.current?.click()}
              >
                {logoPreview ? 'Cambiar imagen' : 'Subir imagen'}
              </Button>
              {logoPreview && (
                <Button type="button" size="small" disabled={disabled} onClick={onLogoClear}>
                  Quitar
                </Button>
              )}
            </div>
          </div>

          <div className="media-block">
            <p className="media-block__label">Fotos del catálogo</p>
            <p className="media-block__hint">
              Arrastra las fotos aquí o haz clic para agregarlas. {catalogCount} de{' '}
              {MAX_CATALOG_IMAGES}. JPG o PNG, máximo 5 Mb cada una.
            </p>
            <label
              className={`media-drop ${catalogDrop.dragOver ? 'is-dragover' : ''} ${
                disabled ? 'is-disabled' : ''
              }`}
              onDragEnter={catalogDrop.onDragEnter}
              onDragOver={catalogDrop.onDragOver}
              onDragLeave={catalogDrop.onDragLeave}
              onDrop={dropCatalog}
              onClick={(event) => {
                if (!catalogFull) return;
                event.preventDefault();
                Swal.fire('Puedes subir máximo 5 fotos del catálogo.');
              }}
            >
              {catalogCount > 0 ? (
                <div className="media-grid">
                  {catalogItems.map((item, index) => (
                    <div className="media-tile has-image" key={item.key || item.url || index}>
                      <img src={item.preview || item.url} alt={`Foto ${index + 1} del catálogo`} />
                      <button
                        type="button"
                        className="media-tile__remove"
                        disabled={disabled}
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          onCatalogRemove(item.key || item.url);
                        }}
                      >
                        Quitar
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="media-tile">
                  <span>Arrastra o haz clic</span>
                </div>
              )}
              <input
                ref={catalogInputRef}
                type="file"
                accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                multiple
                hidden
                disabled={disabled || catalogFull}
                onChange={readCatalog}
              />
            </label>
            <div className="media-block__actions">
              <Button
                type="button"
                primary
                size="small"
                disabled={disabled || catalogFull}
                onClick={() => catalogInputRef.current?.click()}
              >
                Agregar fotos
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NetworkFormMedia;
